import ExcelJS, { Column } from 'exceljs';
import { saveAs } from 'file-saver';
import { isNonEmptyArray } from 'ramda-adjunct';

export const getStartIndexFromChar = (char: string) => {
	if (char.length === 1 && /[A-Z]/.test(char)) {
		return char.toUpperCase().charCodeAt(0) - 64;
	} else {
		throw new Error('Please provide a single uppercase letter from A to Z.');
	}
};

export const numberToLettersForExcel = (n: number, char: string = 'A') => {
	let result = '';

	const startIndex = getStartIndexFromChar(char);
	let number = startIndex + n - 1;

	while (number > 0) {
		let remainder = (number - 1) % 26;
		result = String.fromCharCode(65 + remainder) + result;
		number = Math.floor((number - 1) / 26);
	}
	return result;
};

export type TDownloadExcelLocal = {
	columns?: Array<Partial<Column>>;
	rows?: readonly any[];
	fileName?: string;
	mergeCell?: {
		from: number;
		to: number;
		name?: string;
	}[];
};

const _alignment: Partial<ExcelJS.Alignment> = {
	vertical: 'middle', // Vertically centered
	// horizontal: 'center', // Horizontally centered
	wrapText: true, // Text wrapping
};

export const downloadExcelLocal = async ({ ...props }: TDownloadExcelLocal) => {
	if (!props.columns) return;

	try {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet();

		worksheet.columns = props.columns;

		// MergeCell
		if (props?.mergeCell && isNonEmptyArray(props?.mergeCell)) {
			worksheet.duplicateRow(1, 1, true);
			worksheet.getCell('A1').value = '';

			for (const cell of Object.values(props.mergeCell)) {
				// Start with column B because column A is the ID
				const from = `${numberToLettersForExcel(cell.from, 'B')}1`;
				const to = `${numberToLettersForExcel(cell.to, 'B')}1`;
				const mergeCells = `${from}:${to}`;

				worksheet.mergeCells(mergeCells);
				worksheet.getCell(from).value = cell.name;
			}
		}

		const rows = [...(props?.rows || [])];
		worksheet.addRows(rows);

		worksheet.eachRow((row, rowNumber) => {
			row.eachCell((cell, colNumber) => {
				cell.alignment = _alignment;
			});
		});

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		saveAs(blob, props?.fileName || 'My Sheet');
	} catch (error) {
		console.error(error);
	}
};
